import { Component, EventEmitter, Input, OnChanges, Output, SimpleChange, SimpleChanges } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { NonprofitSearchFacet } from '@core/models/npo.model';
import { SpinnerService } from '@core/services/spinner.service';
import { ALL_SKIP_FILTER, DebounceFactory, SearchByType, SearchFilter, SearchResult, TypeToken } from '@yourcause/common';
import { TypeaheadSelectOption } from '@yourcause/common/core-forms';
import { I18nService } from '@yourcause/common/i18n';
import { uniqBy } from 'lodash';
import { from } from 'rxjs';
import { NonprofitSearchService } from './nonprofit-search.service';

@Component({
    selector: 'yc-nonprofit-search',
    templateUrl: './nonprofit-search.component.html',
    styleUrls: ['./nonprofit-search.component.scss'],
    standalone: false
})
export class NonprofitSearchComponent implements OnChanges {
  @Input() setSearchTermNull: boolean; // applies to Search pages that have actions on Nonprofit
  @Output() returnResults = new EventEmitter<SearchResult[]>();

  searchFiltersShowAll: boolean;
  searchFiltersShowChapters: boolean;
  searchFiltersRegAuth: string;
  searchFiltersCountry: string;
  searchFiltersState: string;
  term: string;
  searchType: number;
  placeholderText = '';
  SearchByType = SearchByType;
  loadingCountryOptions = true;
  loadingStateOptions = true;
  regAuthOptions: TypeaheadSelectOption[] = [];
  countryOptions: TypeaheadSelectOption[] = [];
  stateProvRegionOptions: TypeaheadSelectOption[] = [];
  searchFilters: SearchFilter[] = [];
  advancedFilterForm: UntypedFormGroup;
  formArray: UntypedFormArray;
  searchForm: UntypedFormGroup;
  disableFilterCheckboxes: boolean;
  facets: NonprofitSearchFacet;
  activeFacets: NonprofitSearchFacet;
  debounce: DebounceFactory<any>;
  formGroupTypeToken = new TypeToken<UntypedFormGroup>();

  showInactiveText = this.i18n.translate(
    'search:lblShowInactiveNonprofitsCheckbox',
    {},
    'Show inactive nonprofits'
  );
  byNonprofitPlaceholder = this.i18n.translate(
    'search:textSearchByregIdNameLocationPlaceholder',
    {},
    'Search by Registration ID, Name or Location'
  );
  byAdminPlaceholder = this.i18n.translate(
    'search:textSearchByNonprofitAdminEmail',
    {},
    'Search by NPO admin email address'
  );
  byPaymentPlaceholder = this.i18n.translate(
    'search:textSearchByNonprofitPayment',
    {},
    'Search by nonprofit payment number'
  );
  defaultRegAuthText = this.i18n.translate(
    'search:textAllRegAuths',
    {},
    '-- All registration authorities --'
  );
  defaultCountryText = this.i18n.translate(
    'search:textAllCountries',
    {},
    '-- All countries --'
  );
  defaultStateText = this.i18n.translate(
    'search:textAllStateProvRegs',
    {},
    '-- All states, provinces or regions --'
  );

  searchByTypeOptions: TypeaheadSelectOption[] = [{
    value: SearchByType.TERM,
    label: this.i18n.translate('common:textByNonprofit', {}, 'By nonprofit')
  }, {
    value: SearchByType.ADMIN,
    label: this.i18n.translate('common:textByAdminEmail', {}, 'By admin email')
  }, {
    value: SearchByType.PAYMENT,
    label: this.i18n.translate('common:textByPaymentNumber', {}, 'By payment number')
  }];

  constructor (
    private formBuilder: UntypedFormBuilder,
    private nonprofitSearchService: NonprofitSearchService,
    private i18n: I18nService,
    private spinnerService: SpinnerService
  ) {
    this.searchForm = this.formBuilder.group({
      term: this.term || '',
      searchType: this.searchType || SearchByType.TERM,
      showInactive: this.searchFiltersShowAll,
      showChapters: this.searchFiltersShowChapters
    });
    this.advancedFilterForm = this.formBuilder.group({
      registrationAuthority: [this.searchFiltersRegAuth || ALL_SKIP_FILTER],
      country: [this.searchFiltersCountry || ALL_SKIP_FILTER],
      stateProvRegName: [this.searchFiltersState || ALL_SKIP_FILTER]
    });

    this.formArray = formBuilder.array([
      this.searchForm,
      this.advancedFilterForm
    ]);
    this.setPlaceholderText(this.searchForm.value.searchType);
    this.debounce = DebounceFactory.create<any>(() => {
      return from(this.doSearch());
    }, 500);
  }

  async doSearch () {
    const searchType = this.nonprofitSearchService.searchType;

    this.spinnerService.startSpinner();

    switch (searchType) {
      case SearchByType.ADMIN:
        await this.nonprofitSearchService.searchByAdminEmail();
        break;
      case SearchByType.PAYMENT:
        await this.nonprofitSearchService.searchByPaymentNumber();
        break;
      case SearchByType.TERM:
      default:
        await this.nonprofitSearchService.searchByTerm();
    }

    this.facets = this.nonprofitSearchService.allFacets;

    if (!this.searchFiltersRegAuth) {
      this.setRegAuthOptions(this.facets);
    }
    if (this.searchFiltersRegAuth && !this.searchFiltersCountry) {
      this.setCountryOptions(this.facets);
    }
    if (this.searchFiltersCountry && !this.searchFiltersState) {
      this.setStateOptions(this.facets);
    }
    this.spinnerService.stopSpinner();
  }

  _onTermChange (event: string) {
    this.term = event;
    this.searchType = this.searchForm.value.searchType as SearchByType;
    this.searchFiltersShowAll = this.formArray.at(0).value.showInactive;
    this.searchFiltersShowChapters = this.formArray.at(0).value.showChapters;

    this.nonprofitSearchService.setSearchType(this.searchType);
    this.nonprofitSearchService.setSearchTerm(event, this.searchType);
    this.nonprofitSearchService.setSearchFilters(
      this.searchFiltersShowAll,
      this.searchFiltersShowChapters,
      this.searchFiltersRegAuth,
      this.searchFiltersCountry,
      this.searchFiltersState
    );

    if (!!event) {
      if (event !== this.term) {
        this.clearAdvancedFilters();
      }
      this.debounce.exec(event)
        .subscribe((results) => {
          if (results?.length <= 1
            &&
            !(this.searchFiltersRegAuth ||
              this.searchFiltersCountry ||
              this.searchFiltersState)
          ) {
            this.facets = null;
          }
          this.returnResults.emit(this.nonprofitSearchService.searchResults);
      });
    } else if (!event || event === '') {
      this.clearAdvancedFilters();
      this.nonprofitSearchService.setPaymentNumber(null);
      this.nonprofitSearchService.setSearchType(null);
      this.returnResults.emit([]);
    }
  }

  _changeType () {
    if (this.searchForm.value.searchType === SearchByType.ADMIN ||
      this.searchForm.value.searchType === SearchByType.PAYMENT) {
        this.disableFilterCheckboxes = true;
        this.searchForm.get('term').setValue('');
    } else {
      this.searchFiltersShowAll = this.searchForm.value.showInactive;
      this.searchFiltersShowChapters = this.searchForm.value.showChapters;
      this.disableFilterCheckboxes = false;
    }
    this.clearAdvancedFilters();
    this.setPlaceholderText(this.searchForm.value.searchType);
    this._onTermChange(this.searchForm.value.term);
  }

  _changeFilter (
    event: string,
    type: 'regAuth'|'country'|'state'
  ) {
    if (event && type) {
      switch (type) {
        default:
        case 'regAuth':
          this.searchFiltersRegAuth = event;
          this.formArray.at(1).get('country').setValue(ALL_SKIP_FILTER);
          this.formArray.at(1).get('stateProvRegName').setValue(ALL_SKIP_FILTER);
          this.countryOptions = [];
          this.stateProvRegionOptions = [];
          this.searchFiltersCountry = '';
          this.searchFiltersState = '';
          this.loadingCountryOptions = true;
          break;
        case 'country':
          this.searchFiltersCountry = event;
          this.formArray.at(1).get('stateProvRegName').setValue(ALL_SKIP_FILTER);
          this.stateProvRegionOptions = [];
          this.searchFiltersState = '';
          this.loadingStateOptions = true;
          break;
        case 'state':
          this.searchFiltersState = event;
          break;
      }
      this._onTermChange(this.term);
    } else if (!event && type) {
      switch (type) {
        default:
        case 'regAuth':
          this.clearAdvancedFilters();
          break;
        case 'country':
          this.searchFiltersCountry = '';
          this.searchFiltersState = '';
          this.stateProvRegionOptions = [];
          this.formArray.at(1).get('country').setValue(ALL_SKIP_FILTER);
          this.formArray.at(1).get('stateProvRegName').setValue(ALL_SKIP_FILTER);
          this.loadingStateOptions = false;
          break;
        case 'state':
          this.searchFiltersState = '';
          this.formArray.at(1).get('stateProvRegName').setValue(ALL_SKIP_FILTER);
          break;
      }
      this._onTermChange(this.term);
    } else if (!event && !type) {
      this.returnResults.emit([]);
      this.clearAdvancedFilters();
    }
  }

  clearAdvancedFilters () {
    this.facets = null;
    this.regAuthOptions = [];
    this.countryOptions = [];
    this.stateProvRegionOptions = [];
    this.searchFiltersRegAuth = '';
    this.searchFiltersCountry = '';
    this.searchFiltersState = '';
    this.formArray.at(1).get('registrationAuthority').setValue(ALL_SKIP_FILTER);
    this.formArray.at(1).get('country').setValue(ALL_SKIP_FILTER);
    this.formArray.at(1).get('stateProvRegName').setValue(ALL_SKIP_FILTER);
  }

  setRegAuthOptions (facets: NonprofitSearchFacet) {
    this.regAuthOptions = [{
      label: this.defaultRegAuthText,
      value: ALL_SKIP_FILTER
    }];
    if (facets) {
      facets.registrationAuthority.forEach((facet) => {
        this.regAuthOptions.push({
          label: facet.value,
          value: facet.value
        });
      });
      this.regAuthOptions = uniqBy(this.regAuthOptions, (opt) => opt.label && opt.value);
    }
  }

  setCountryOptions (facets: NonprofitSearchFacet) {
    this.countryOptions = [{
      label: this.defaultCountryText,
      value: ALL_SKIP_FILTER
    }];
    if (facets) {
      facets.country.forEach((facet) => {
        this.countryOptions.push({
          label: facet.value,
          value: facet.value
        });
      });
      this.countryOptions = uniqBy(this.countryOptions, (opt) => opt.label && opt.value);
      this.loadingCountryOptions = false;
    }
  }

  setStateOptions (facets: NonprofitSearchFacet) {
    this.stateProvRegionOptions = [{
      label: this.defaultStateText,
      value: ALL_SKIP_FILTER
    }];
    if (facets) {
      facets.stateProvRegName.forEach((facet) => {
        this.stateProvRegionOptions.push({
          label: facet.value,
          value: facet.value
        });
      });
      this.stateProvRegionOptions = uniqBy(this.stateProvRegionOptions, (opt) => opt.label && opt.value);
      this.loadingStateOptions = false;
    }
  }

   setPlaceholderText (searchType: SearchByType) {
    switch (searchType) {
      case SearchByType.ADMIN:
        this.placeholderText = this.byAdminPlaceholder;
        break;
      case SearchByType.PAYMENT:
        this.placeholderText = this.byPaymentPlaceholder;
        break;
      default:
      case SearchByType.TERM:
        this.placeholderText = this.byNonprofitPlaceholder;
    }
  }

  ngOnChanges (changes: SimpleChanges) {
    const change: SimpleChange = changes['setSearchTermNull'];
    if (change.currentValue) {
      this.formArray.at(0).get('term').setValue('');
    }
  }
}
